import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, getToken, setToken } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    api
      .get('/api/auth/me')
      .then(({ user: me }) => setUser(me))
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  const applySession = useCallback((session) => {
    setToken(session.token);
    setUser(session.user);
    return session.user;
  }, []);

  const login = useCallback(
    async (email, password) => applySession(await api.post('/api/auth/login', { email, password })),
    [applySession]
  );

  // Registering no longer signs you in directly — the account is not
  // verified yet, so this just kicks off the email-code step. The response
  // is { pendingVerification: true, email }.
  const registerNanny = useCallback((data) => api.post('/api/auth/register/nanny', data), []);
  const registerFamily = useCallback((data) => api.post('/api/auth/register/family', data), []);

  const verifyEmail = useCallback(
    async (email, code) => applySession(await api.post('/api/auth/verify-email', { email, code })),
    [applySession]
  );

  const resendCode = useCallback((email) => api.post('/api/auth/resend-code', { email }), []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, registerNanny, registerFamily, verifyEmail, resendCode, setUser }),
    [user, loading, login, logout, registerNanny, registerFamily, verifyEmail, resendCode]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider.');
  return ctx;
}

/** Where each role lands after signing in. Team accounts use the admin dashboard. */
export const HOME_FOR_ROLE = {
  nanny: '/nanny',
  family: '/family',
};
